import React, { useState, useRef, useEffect } from "react";
import { FaFileCsv, FaFileExcel, FaClipboard, FaCheckCircle, FaTimesCircle, FaChevronRight, FaChevronDown, FaCode, FaTable, FaShopify } from "react-icons/fa";

const ExportMenu = ({
  onExportCSV,
  onExportExcel,
  onExportJSON,
  onExportShopify,
  onCopyToClipboard,
  onExportToGoogleSheets,
  onExportMenuOpened,
  isExporting,
  exportingFormat = null,
  exportMessage,
  canExport,
  exportStats,
  isDarkMode = true,
  onUpgradeClick,
  totalRowCount,
  clipboardDisabled = false,
  clipboardDisabledReason = null,
  googleSheetsStatus = null,
  isShopifyTable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keep menu open during export - it will close when export completes
  const handleOptionClick = (exportFunction) => {
    exportFunction();
    // Don't close menu - let it stay open to show progress
  };

  const handleCopyToClipboard = async () => {
    await onCopyToClipboard();
    // Don't close menu - let it stay open to show progress
  };

  // Close menu when export completes (success or error, but not loading)
  React.useEffect(() => {
    if (exportMessage && exportMessage.type !== "loading" && isOpen) {
      // Small delay to let user see the completion state
      const timer = setTimeout(() => setIsOpen(false), 800);
      return () => clearTimeout(timer);
    }
  }, [exportMessage, isOpen]);

  // Helper to render loading spinner with color variant
  const LoadingSpinner = ({ color = "blue" }) => {
    const colorClasses = {
      blue: isDarkMode ? "border-blue-400" : "border-blue-600",
      emerald: isDarkMode ? "border-emerald-400" : "border-emerald-600",
      violet: isDarkMode ? "border-violet-400" : "border-violet-600",
      amber: isDarkMode ? "border-amber-400" : "border-amber-600",
    };
    return (
      <div className={`w-4 h-4 border-2 border-t-transparent rounded-full animate-spin ${colorClasses[color]}`} />
    );
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Export Button */}
      <button
        onClick={() => {
          const newIsOpen = !isOpen;
          setIsOpen(newIsOpen);

          if (newIsOpen && onExportMenuOpened) {
            onExportMenuOpened();
          }
        }}
        disabled={!canExport || isExporting}
        className={`
          px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2
          transition-all duration-200 min-w-[100px] justify-center
          flex-shrink-0 whitespace-nowrap
          ${
            !canExport || isExporting
              ? isDarkMode
                ? "bg-white/[0.04] text-gray-500 border border-white/[0.06]"
                : "bg-gray-100 text-gray-400 border border-gray-200"
              : isDarkMode
                ? "bg-white text-black hover:bg-gray-100 active:scale-[0.98]"
                : "bg-gray-900 text-white hover:bg-gray-800 active:scale-[0.98]"
          }
        `}
        title={!canExport ? "No data to export" : "Export data"}
      >
        <div className="flex items-center gap-2">
          {isExporting ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
              <span>Exporting...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Export</span>
              <FaChevronDown className={`w-2.5 h-2.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
            </>
          )}
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && canExport && (
        <div className="absolute right-0 top-full mt-2 w-64 z-[300] animate-scale-in">
          <div className={`rounded-xl shadow-2xl border overflow-hidden transition-colors duration-200 ${
            isDarkMode
              ? "bg-[#1a1a1c] border-white/[0.08]"
              : "bg-white border-gray-200 shadow-xl"
          }`}>
            {/* Header with row count */}
            {exportStats && (
              <div className={`px-4 py-3 border-b ${isDarkMode ? "border-white/[0.06]" : "border-gray-100"} ${isExporting ? "relative overflow-hidden" : ""}`}>
                {/* Progress bar animation when exporting */}
                {isExporting && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent animate-shimmer" />
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                      {isExporting ? "Exporting..." : "Export Data"}
                    </h3>
                    <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                      {isExporting ? "Please wait" : "Choose a format"}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-semibold tabular-nums ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                      {(totalRowCount !== undefined && totalRowCount !== null ? totalRowCount : exportStats.totalRows).toLocaleString()}
                    </div>
                    <div className={`text-[10px] uppercase tracking-wider ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                      {(totalRowCount !== undefined && totalRowCount !== null ? totalRowCount : exportStats.totalRows) === 1 ? "Row" : "Rows"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Export Options */}
            <div className="p-1.5">
              {/* CSV Export */}
              <button
                onClick={() => handleOptionClick(onExportCSV)}
                disabled={isExporting}
                className={`group w-full px-3 py-2.5 rounded-lg text-left flex items-center gap-3 transition-all duration-150 ${
                  isExporting && exportingFormat !== 'csv'
                    ? "opacity-40 cursor-not-allowed"
                    : exportingFormat === 'csv'
                      ? isDarkMode ? "bg-blue-500/10" : "bg-blue-50"
                      : isDarkMode ? "hover:bg-white/[0.06]" : "hover:bg-gray-50"
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                  exportingFormat === 'csv'
                    ? isDarkMode ? "bg-blue-500/25" : "bg-blue-100"
                    : isDarkMode ? "bg-blue-500/15" : "bg-blue-50"
                }`}>
                  {exportingFormat === 'csv' ? (
                    <LoadingSpinner color="blue" />
                  ) : (
                    <FaFileCsv className={`text-sm ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium text-sm ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>CSV</div>
                  <div className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                    {exportingFormat === 'csv' ? "Preparing download..." : "Universal format"}
                  </div>
                </div>
                {exportingFormat !== 'csv' && (
                  <FaChevronRight className={`w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`} />
                )}
              </button>

              {/* Excel Export */}
              <button
                onClick={() => handleOptionClick(onExportExcel)}
                disabled={isExporting}
                className={`group w-full px-3 py-2.5 rounded-lg text-left flex items-center gap-3 transition-all duration-150 ${
                  isExporting && exportingFormat !== 'excel'
                    ? "opacity-40 cursor-not-allowed"
                    : exportingFormat === 'excel'
                      ? isDarkMode ? "bg-emerald-500/10" : "bg-emerald-50"
                      : isDarkMode ? "hover:bg-white/[0.06]" : "hover:bg-gray-50"
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                  exportingFormat === 'excel'
                    ? isDarkMode ? "bg-emerald-500/25" : "bg-emerald-100"
                    : isDarkMode ? "bg-emerald-500/15" : "bg-emerald-50"
                }`}>
                  {exportingFormat === 'excel' ? (
                    <LoadingSpinner color="emerald" />
                  ) : (
                    <FaFileExcel className={`text-sm ${isDarkMode ? "text-emerald-400" : "text-emerald-600"}`} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium text-sm ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>Excel</div>
                  <div className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                    {exportingFormat === 'excel' ? "Preparing download..." : ".xlsx workbook"}
                  </div>
                </div>
                {exportingFormat !== 'excel' && (
                  <FaChevronRight className={`w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`} />
                )}
              </button>

              {/* JSON Export */}
              <button
                onClick={() => handleOptionClick(onExportJSON)}
                disabled={isExporting}
                className={`group w-full px-3 py-2.5 rounded-lg text-left flex items-center gap-3 transition-all duration-150 ${
                  isExporting && exportingFormat !== 'json'
                    ? "opacity-40 cursor-not-allowed"
                    : exportingFormat === 'json'
                      ? isDarkMode ? "bg-violet-500/10" : "bg-violet-50"
                      : isDarkMode ? "hover:bg-white/[0.06]" : "hover:bg-gray-50"
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                  exportingFormat === 'json'
                    ? isDarkMode ? "bg-violet-500/25" : "bg-violet-100"
                    : isDarkMode ? "bg-violet-500/15" : "bg-violet-50"
                }`}>
                  {exportingFormat === 'json' ? (
                    <LoadingSpinner color="violet" />
                  ) : (
                    <FaCode className={`text-sm ${isDarkMode ? "text-violet-400" : "text-violet-600"}`} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium text-sm ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>JSON</div>
                  <div className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                    {exportingFormat === 'json' ? "Preparing download..." : "Developer format"}
                  </div>
                </div>
                {exportingFormat !== 'json' && (
                  <FaChevronRight className={`w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`} />
                )}
              </button>

              {/* Shopify product-import CSV — only for Shopify-extraction tables */}
              {isShopifyTable && (
                <button
                  onClick={() => handleOptionClick(onExportShopify)}
                  disabled={isExporting}
                  title="Re-import-ready Shopify product CSV (grouped by Handle)"
                  className={`group w-full px-3 py-2.5 rounded-lg text-left flex items-center gap-3 transition-all duration-150 ${
                    isExporting && exportingFormat !== 'shopify'
                      ? "opacity-40 cursor-not-allowed"
                      : exportingFormat === 'shopify'
                        ? isDarkMode ? "bg-shopify-500/10" : "bg-shopify-50"
                        : isDarkMode ? "hover:bg-white/[0.06]" : "hover:bg-gray-50"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                    exportingFormat === 'shopify'
                      ? isDarkMode ? "bg-shopify-500/25" : "bg-shopify-100"
                      : isDarkMode ? "bg-shopify-500/15" : "bg-shopify-50"
                  }`}>
                    {exportingFormat === 'shopify' ? (
                      <div className={`w-4 h-4 border-2 border-t-transparent rounded-full animate-spin ${isDarkMode ? "border-shopify-400" : "border-shopify-600"}`} />
                    ) : (
                      <FaShopify className={`text-sm ${isDarkMode ? "text-shopify-400" : "text-shopify-600"}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium text-sm ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>Shopify CSV</div>
                    <div className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                      {exportingFormat === 'shopify' ? "Preparing download..." : "Product-import format"}
                    </div>
                  </div>
                  {exportingFormat !== 'shopify' && (
                    <FaChevronRight className={`w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`} />
                  )}
                </button>
              )}

              {/* Google Sheets Export */}
              {googleSheetsStatus?.connected ? (
                <button
                  onClick={() => handleOptionClick(onExportToGoogleSheets)}
                  disabled={isExporting}
                  className={`group w-full px-3 py-2.5 rounded-lg text-left flex items-center gap-3 transition-all duration-150 ${
                    isExporting && exportingFormat !== 'googlesheets'
                      ? "opacity-40 cursor-not-allowed"
                      : exportingFormat === 'googlesheets'
                        ? isDarkMode ? "bg-green-500/10" : "bg-green-50"
                        : isDarkMode ? "hover:bg-white/[0.06]" : "hover:bg-gray-50"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                    exportingFormat === 'googlesheets'
                      ? isDarkMode ? "bg-green-500/25" : "bg-green-100"
                      : isDarkMode ? "bg-green-500/15" : "bg-green-50"
                  }`}>
                    {exportingFormat === 'googlesheets' ? (
                      <LoadingSpinner color="emerald" />
                    ) : (
                      <FaTable className={`text-sm ${isDarkMode ? "text-green-400" : "text-green-600"}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium text-sm ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>Google Sheets</div>
                    <div className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                      {exportingFormat === 'googlesheets' ? "Creating spreadsheet..." : googleSheetsStatus.email || "Export to new spreadsheet"}
                    </div>
                  </div>
                  {exportingFormat !== 'googlesheets' && (
                    <FaChevronRight className={`w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`} />
                  )}
                </button>
              ) : (
                <button
                  disabled={true}
                  title={googleSheetsStatus === null ? "Checking connection..." : "Connect Google Sheets in Settings"}
                  className={`group w-full px-3 py-2.5 rounded-lg text-left flex items-center gap-3 transition-all duration-150 opacity-50 cursor-not-allowed`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    isDarkMode ? "bg-green-500/15" : "bg-green-50"
                  }`}>
                    <FaTable className={`text-sm ${isDarkMode ? "text-green-400" : "text-green-600"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium text-sm ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>Google Sheets</div>
                    <div className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                      {googleSheetsStatus === null ? "Checking..." : "Connect in Settings"}
                    </div>
                  </div>
                </button>
              )}

              {/* Separator */}
              <div className={`my-1.5 border-t ${isDarkMode ? "border-white/[0.06]" : "border-gray-100"}`}></div>

              {/* Copy to Clipboard */}
              <button
                onClick={() => !clipboardDisabled && !isExporting && handleCopyToClipboard()}
                disabled={clipboardDisabled || isExporting}
                title={clipboardDisabledReason || "Copy all rows to clipboard"}
                className={`group w-full px-3 py-2.5 rounded-lg text-left flex items-center gap-3 transition-all duration-150 ${
                  clipboardDisabled || (isExporting && exportingFormat !== 'clipboard')
                    ? "opacity-40 cursor-not-allowed"
                    : exportingFormat === 'clipboard'
                      ? isDarkMode ? "bg-amber-500/10" : "bg-amber-50"
                      : isDarkMode ? "hover:bg-white/[0.06]" : "hover:bg-gray-50"
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                  exportingFormat === 'clipboard'
                    ? isDarkMode ? "bg-amber-500/25" : "bg-amber-100"
                    : isDarkMode ? "bg-amber-500/15" : "bg-amber-50"
                }`}>
                  {exportingFormat === 'clipboard' ? (
                    <LoadingSpinner color="amber" />
                  ) : (
                    <FaClipboard className={`text-sm ${isDarkMode ? "text-amber-400" : "text-amber-600"}`} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium text-sm ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>Copy to Clipboard</div>
                  <div className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                    {exportingFormat === 'clipboard' ? "Copying data..." : clipboardDisabled ? "Over 1,000 rows" : "Quick copy"}
                  </div>
                </div>
                {!clipboardDisabled && exportingFormat !== 'clipboard' && (
                  <FaChevronRight className={`w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`} />
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Message Toast */}
      {exportMessage && (
        <div className="fixed bottom-4 right-4 z-[400] max-w-sm animate-slide-in-right">
          <div
            className={`
              px-4 py-3 rounded-xl shadow-2xl border transition-all duration-300
              ${exportMessage.type === "success"
                ? isDarkMode
                  ? "bg-[#0d2818] border-emerald-500/30 text-emerald-100"
                  : "bg-emerald-100 border-emerald-300 text-emerald-900"
                : exportMessage.type === "loading"
                  ? isDarkMode
                    ? "bg-[#1a1a2e] border-blue-500/30 text-blue-100"
                    : "bg-blue-50 border-blue-200 text-blue-900"
                  : isDarkMode
                    ? "bg-[#2a1215] border-red-500/30 text-red-100"
                    : "bg-red-100 border-red-300 text-red-900"
              }
            `}
          >
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                exportMessage.type === "success"
                  ? isDarkMode ? "bg-emerald-500/20" : "bg-emerald-100"
                  : exportMessage.type === "loading"
                    ? isDarkMode ? "bg-blue-500/20" : "bg-blue-100"
                    : isDarkMode ? "bg-red-500/20" : "bg-red-100"
              }`}>
                {exportMessage.type === "success"
                  ? <FaCheckCircle className={`text-sm ${isDarkMode ? "text-emerald-400" : "text-emerald-600"}`} />
                  : exportMessage.type === "loading"
                    ? <div className={`w-4 h-4 border-2 border-t-transparent rounded-full animate-spin ${isDarkMode ? "border-blue-400" : "border-blue-600"}`} />
                    : <FaTimesCircle className={`text-sm ${isDarkMode ? "text-red-400" : "text-red-600"}`} />
                }
              </div>
              <div>
                <div className="font-semibold text-sm">
                  {exportMessage.type === "success" ? "Export Complete" : exportMessage.type === "loading" ? "Exporting..." : "Export Failed"}
                </div>
                <div className={`text-xs ${isDarkMode ? "opacity-70" : "opacity-80"}`}>{exportMessage.text}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style>{`
        @keyframes scale-in {
          0% {
            transform: scale(0.96) translateY(-4px);
            opacity: 0;
          }
          100% {
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        }

        @keyframes slide-in-right {
          0% {
            transform: translateX(100%);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .animate-scale-in {
          animation: scale-in 0.15s ease-out;
        }

        .animate-slide-in-right {
          animation: slide-in-right 0.25s ease-out;
        }

        .animate-shimmer {
          animation: shimmer 1.5s ease-in-out infinite;
        }

        /* Enhanced focus states for accessibility */
        button:focus-visible {
          outline: 2px solid currentColor;
          outline-offset: 2px;
          opacity: 0.8;
        }
      `}</style>
    </div>
  );
};

export default ExportMenu;
